import React, { createContext, useContext, useEffect, useState } from "react";

interface CacheContextType {
  cachedAssets: Map<string, string>;
  addToCache: (key: string, value: string) => void;
  getFromCache: (key: string) => string | undefined;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export function CacheProvider({ children }: { children: React.ReactNode }) {
  const [cachedAssets, setCachedAssets] = useState<Map<string, string>>(
    new Map()
  );

  // Load cached assets from localStorage on mount
  useEffect(() => {
    const savedCache = localStorage.getItem("assetCache");
    if (savedCache) {
      try {
        const parsedCache = new Map(
          JSON.parse(savedCache) as [string, string][]
        );
        setCachedAssets(parsedCache);
      } catch (error) {
        console.error("Error loading cache:", error);
      }
    }
  }, []);

  // Save cache to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "assetCache",
        JSON.stringify(Array.from(cachedAssets.entries()))
      );
    } catch (error) {
      console.error("Error saving cache:", error);
    }
  }, [cachedAssets]);

  const addToCache = (key: string, value: string) => {
    setCachedAssets((prev) => {
      const newCache = new Map(prev);
      newCache.set(key, value);
      return newCache;
    });
  };

  const getFromCache = (key: string) => {
    return cachedAssets.get(key);
  };

  return (
    <CacheContext.Provider value={{ cachedAssets, addToCache, getFromCache }}>
      {children}
    </CacheContext.Provider>
  );
}

export function useCache() {
  const context = useContext(CacheContext);
  if (context === undefined) {
    throw new Error("useCache must be used within a CacheProvider");
  }
  return context;
}
